import { useEffect, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Eye, Trash2 } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";

export const VAULT_TYPES = ["RC", "Insurance", "Permit", "FC", "DL", "PUC"];

export const VehicleVaultDialog = ({ vehicleNo, onClose, onChanged }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState("");
  const load = (vno) => api.get(`/vehicle-docs/${encodeURIComponent(vno)}`).then((r) => setDocs(r.data || [])).catch(() => setDocs([]));
  useEffect(() => {
    if (vehicleNo) load(vehicleNo);
  }, [vehicleNo]);
  if (!vehicleNo) return null;
  const upload = async (dt, file) => {
    if (!file) return;
    setBusy(dt);
    try {
      const fd = new FormData();
      fd.append("doc_type", dt);
      fd.append("file", file);
      await api.post(`/vehicle-docs/${encodeURIComponent(vehicleNo)}`, fd);
      toast.success(`${dt} copy attached for ${vehicleNo}`);
      load(vehicleNo);
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };
  const setDate = async (dt, val) => {
    try {
      await api.post(`/vehicle-docs/${encodeURIComponent(vehicleNo)}/${dt}/valid-upto`, { valid_upto: val || "" });
      setDocs((ds) => ds.map((d) => (d.doc_type === dt ? { ...d, valid_upto: val || "" } : d)));
      toast.success(`${dt} valid-upto saved — auto-fills on new challans of ${vehicleNo}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const remove = async (dt) => {
    if (!window.confirm(`Remove the ${dt} copy of ${vehicleNo}?`)) return;
    try {
      await api.delete(`/vehicle-docs/${encodeURIComponent(vehicleNo)}/${dt}`);
      toast.success(`${dt} copy removed`);
      load(vehicleNo);
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-[640px]" data-testid="vehicle-vault-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Vehicle Document Vault — {vehicleNo}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">Scanned RC, Insurance, Permit, FC, DL and PUC copies stay with the vehicle — visible on every challan of {vehicleNo}.</p>
        <div className="rounded border">
          <table className="w-full text-xs">
            <thead className="bg-muted">
              <tr className="text-left">
                <th className="p-2">Document</th>
                <th className="p-2">Status</th>
                <th className="p-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {VAULT_TYPES.map((dt) => {
                const doc = docs.find((d) => d.doc_type === dt);
                return (
                  <tr key={dt} className="border-t" data-testid={`vault-row-${dt.toLowerCase()}`}>
                    <td className="p-2 font-bold">{dt === "RC" ? "RC (Registration)" : dt === "FC" ? "FC (Fitness)" : dt === "DL" ? "DL (Driver Licence)" : dt === "PUC" ? "PUC (Pollution)" : dt}</td>
                    <td className="p-2">
                      {doc ? (
                        <span className="inline-flex flex-wrap items-center gap-1.5">
                          {/\.(jpe?g|png|webp)$/i.test(doc.filename || "") ? (
                            <img
                              src={`${API}/files/${doc.path}?auth=${localStorage.getItem("ntc_token")}`}
                              alt={dt}
                              loading="lazy"
                              className="h-9 w-9 cursor-pointer rounded border object-cover"
                              onClick={() => window.open(`${API}/files/${doc.path}?auth=${localStorage.getItem("ntc_token")}`, "_blank")}
                              data-testid={`vault-thumb-${dt.toLowerCase()}`}
                            />
                          ) : null}
                          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700" title={doc.filename}>
                            Attached {fmtDate(String(doc.at || "").slice(0, 10))}{doc.by ? ` · ${doc.by}` : ""}
                          </span>
                          {dt !== "__never__" ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500">
                              Valid upto
                              <input
                                type="date"
                                className="h-6 rounded border border-slate-200 px-1 text-[10px]"
                                value={String(doc.valid_upto || "").slice(0, 10)}
                                onChange={(e) => setDate(dt, e.target.value)}
                                data-testid={`vault-date-${dt.toLowerCase()}`}
                              />
                            </span>
                          ) : null}
                        </span>
                      ) : (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">Not attached</span>
                      )}
                    </td>
                    <td className="p-2 text-right">
                      <span className="inline-flex items-center gap-1.5">
                        {doc ? (
                          <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs" onClick={() => window.open(`${API}/files/${doc.path}?auth=${localStorage.getItem("ntc_token")}`, "_blank")} data-testid={`vault-view-${dt.toLowerCase()}`}>
                            <Eye className="mr-1 h-3.5 w-3.5" /> View
                          </Button>
                        ) : null}
                        <label className={`inline-flex h-7 cursor-pointer items-center rounded-md border px-2.5 text-xs font-semibold hover:bg-slate-50 ${busy === dt ? "opacity-50" : ""}`} data-testid={`vault-upload-${dt.toLowerCase()}`}>
                          {busy === dt ? "Uploading…" : doc ? "Replace" : "Upload"}
                          <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" disabled={!!busy} onChange={(e) => { upload(dt, e.target.files?.[0]); e.target.value = ""; }} />
                        </label>
                        {doc ? (
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" title={`Remove ${dt} copy`} onClick={() => remove(dt)} data-testid={`vault-delete-${dt.toLowerCase()}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        ) : null}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};
